export const isPc = () => {
    return window.innerWidth > 767
}

/**
 * 要素が親要素から完全にはみ出していないかを判定する関数
 * 1pxでも親要素に入っていればセーフと判定する
 * 
 * @param {HTMLElement} childElement - チェックしたい子要素
 * @param {HTMLElement} parentElement - 親要素
 * @returns {Object} 判定結果オブジェクト
 * @property {boolean} isCompletelyInside - 完全に内側にあるか
 * @property {boolean} isCompletelyOutside - 完全に外側にあるか
 * @property {Object} overflow - はみ出し情報
 * @property {boolean} overflow.top - 上にはみ出しているか
 * @property {boolean} overflow.bottom - 下にはみ出しているか
 * @property {boolean} overflow.left - 左にはみ出しているか
 * @property {boolean} overflow.right - 右にはみ出しているか
 * @property {Object} overflowAmount - はみ出し量（px）
 * @property {number} overflowAmount.top - 上のはみ出し量（負の値）
 * @property {number} overflowAmount.bottom - 下のはみ出し量（正の値）
 * @property {number} overflowAmount.left - 左のはみ出し量（負の値）
 * @property {number} overflowAmount.right - 右のはみ出し量（正の値）
 */
export const checkElementOverflow = (childElement, parentElement) => {
    // 要素の位置とサイズを取得
    const childRect = childElement.getBoundingClientRect();
    const parentRect = parentElement.getBoundingClientRect();

    // はみ出し量を計算
    const overflowTop = parentRect.top - childRect.top;
    const overflowBottom = childRect.bottom - parentRect.bottom;
    const overflowLeft = parentRect.left - childRect.left;
    const overflowRight = childRect.right - parentRect.right;

    // はみ出し判定
    const overflow = {
        top: overflowTop > 0,
        bottom: overflowBottom > 0,
        left: overflowLeft > 0,
        right: overflowRight > 0
    };

    // 完全に外側にあるかチェック
    const isCompletelyOutside = (
        childRect.right <= parentRect.left ||
        childRect.left >= parentRect.right ||
        childRect.bottom <= parentRect.top ||
        childRect.top >= parentRect.bottom
    );

    // 完全に内側にあるかチェック
    const isCompletelyInside = (
        childRect.top >= parentRect.top &&
        childRect.bottom <= parentRect.bottom &&
        childRect.left >= parentRect.left &&
        childRect.right <= parentRect.right
    );

    return {
        isCompletelyInside,
        isCompletelyOutside,
        overflow,
        overflowAmount: {
            top: overflowTop > 0 ? -overflowTop : 0,
            bottom: overflowBottom > 0 ? overflowBottom : 0,
            left: overflowLeft > 0 ? -overflowLeft : 0,
            right: overflowRight > 0 ? overflowRight : 0
        }
    };
};

/**
 * Intersection Observer を使って最下部到達を検知
 * @param {Function} callback - 最下部到達時に実行される関数
 * @param {number} rootMargin - 検知する範囲のマージン（ピクセル）
 * @example
 * observeScrollToBottom(() => {
 *   console.log('最下部に到達したよ！');
 * }, 100);
 */
export const observeScrollToBottom = (callback, rootMargin = 0) => {
    // 最下部を検知するための要素を作成
    const sentinel = document.createElement('div');
    sentinel.style.height = '1px';
    sentinel.style.position = 'absolute';
    sentinel.style.bottom = '0';
    sentinel.style.left = '0';
    sentinel.style.width = '100%';
    sentinel.style.pointerEvents = 'none';
    sentinel.setAttribute('data-scroll-sentinel', 'true');

    document.body.appendChild(sentinel);

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    callback();
                }
            });
        },
        {
            rootMargin: `${rootMargin}px`
        }
    );

    observer.observe(sentinel);

    // クリーンアップ用の関数を返す
    return () => {
        observer.disconnect();
        if (sentinel.parentNode) {
            sentinel.parentNode.removeChild(sentinel);
        }
    };
};

/**
 * Promise版：全画像読み込み完了を待機
 * @param {string} selector - 対象要素のセレクター
 * @returns {Promise<HTMLImageElement[]>} 読み込み完了した画像の配列
 */
export const loadImagesPromise = (selector) => {
    const imgs = document.querySelectorAll(selector);

    const promises = Array.from(imgs).map(img => {
        return new Promise((resolve, reject) => {
            const targetSrc = img.dataset.src;

            if (img.src !== targetSrc) {
                img.onload = () => resolve(img);
                img.onerror = () => reject(new Error(`Failed to load: ${targetSrc}`));
                img.src = targetSrc;
            } else {
                resolve(img);
            }
        });
    });

    return Promise.all(promises);
};